import React from 'react';
import { BotAgentStage } from './types/chat.types';

/**
 * Compact stage stepper rendered in the bot panel header. Shows the user
 * how far through the booking funnel they are.
 *
 * viewing_order_product is collapsed into selecting_product visually — it's
 * a drill-into-detail side-step within the same step of the funnel.
 */
type StepKey = 'gathering' | 'selecting' | 'cart' | 'checkout';

interface StepDef {
  key: StepKey;
  stages: BotAgentStage[];
}

const STEPS: StepDef[] = [
  { key: 'gathering', stages: ['gathering_search_params'] },
  { key: 'selecting', stages: ['selecting_product', 'viewing_order_product'] },
  { key: 'cart', stages: ['building_cart'] },
  { key: 'checkout', stages: ['checkout_ready', 'order_created'] },
];

const DEFAULT_LABELS: Record<StepKey, string> = {
  gathering: 'Trip basics',
  selecting: 'Browsing',
  cart: 'Cart',
  checkout: 'Checkout',
};

interface Props {
  /** Current stage from bot:done.current_stage. */
  stage: BotAgentStage | null;
  /** Per-step labels — usually pulled from translations. Falls back to English defaults. */
  labels?: Partial<Record<StepKey, string>>;
}

function stageToStepIndex(stage: BotAgentStage | null): number {
  if (!stage) return -1;
  return STEPS.findIndex((s) => s.stages.includes(stage));
}

const StageStepper: React.FC<Props> = ({ stage, labels }) => {
  const activeIdx = stageToStepIndex(stage);
  if (activeIdx < 0) return null;

  const activeKey = STEPS[activeIdx].key;
  const activeLabel = labels?.[activeKey] ?? DEFAULT_LABELS[activeKey];

  return (
    <div className="bot-stepper" aria-label={`Step ${activeIdx + 1} of ${STEPS.length}: ${activeLabel}`}>
      <div className="bot-stepper__dots">
        {STEPS.map((step, i) => {
          const cls =
            i < activeIdx
              ? 'bot-stepper__dot bot-stepper__dot--done'
              : i === activeIdx
              ? 'bot-stepper__dot bot-stepper__dot--active'
              : 'bot-stepper__dot';
          return <span key={step.key} className={cls} />;
        })}
      </div>
      <span className="bot-stepper__label">{activeLabel}</span>
    </div>
  );
};

export default StageStepper;
